/**
 * Synology Surveillance Station + QNAP QVR Pro NAS-based NVR catalog.
 *
 * All specs verified against synology.com / qnap.com product spec PDFs
 * on 2026-05-15. URL cited per entry. Missing specs left undefined.
 *
 * Schema filters applied:
 *  - `codecs` union accepts only 'H.264' / 'H.265' / 'H.265+' / 'Smart 265'
 *    / 'AV1' / 'MJPEG'. Synology datasheets list MPEG-4 + MxPEG too;
 *    those are dropped here.
 *  - `raidLevels` union accepts '0' / '1' / '5' / '6' / '10' / 'JBOD'.
 *    Extended with Synology's 'SHR' / 'SHR-2' / 'Basic' and QNAP's
 *    '50' / '60' / 'Single'.
 *
 * QNAP per-model channel counts are NOT published in QVR Pro
 * documentation (system-wide license cap is 128). Channels field set
 * to 128 with a tagline note. Synology channels match official spec
 * PDF maximums.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const SYNOLOGY_QNAP_NVR_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Synology — Deep-Learning NVR appliances (DVA series)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'synology-dva3221',
    manufacturer: 'Synology',
    model: 'DVA3221',
    category: 'nvr-recorder',
    tagline: 'Deep-learning NVR with 32 channels and up to 12 simultaneous AI tasks',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/ProductSpec/NetworkVideoRecorder/21-year/DVA3221/enu/Product_Spec_DVA3221_enu.pdf',
    specs: {
      channels: 32,
      hddBays: 4,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', 'SHR', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '4× 1GbE RJ-45',
    },
  },

  {
    id: 'synology-dva1622',
    manufacturer: 'Synology',
    model: 'DVA1622',
    category: 'nvr-recorder',
    tagline: 'AI-enabled 2-bay NVR with 16 channels and deep video analytics',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/DataSheet/NetworkVideoRecorder/22-year/DVA1622/enu/Synology_DVA1622_Data_Sheet_enu.pdf',
    specs: {
      channels: 16,
      hddBays: 2,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      raidLevels: ['0', '1', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '1× 1GbE RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Synology — DiskStation Plus / RackStation (Surveillance Station)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'synology-ds923-plus',
    manufacturer: 'Synology',
    model: 'DS923+',
    category: 'nvr-recorder',
    tagline: '4-bay DSM NAS running Surveillance Station with up to 40 IP cameras',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/DataSheet/DiskStation/23-year/DS923+/enu/Synology_DS923+_Data_Sheet_enu.pdf',
    specs: {
      channels: 40,
      hddBays: 4,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', 'SHR', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ-45',
    },
  },

  {
    id: 'synology-ds1522-plus',
    manufacturer: 'Synology',
    model: 'DS1522+',
    category: 'nvr-recorder',
    tagline: '5-bay Plus NAS with quad 1GbE running Surveillance Station up to 40 cameras',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/ProductSpec/DiskStation/22-year/DS1522+/enu/Product_Spec_DS1522+_enu.pdf',
    specs: {
      channels: 40,
      hddBays: 5,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', 'SHR', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '4× 1GbE RJ-45',
    },
  },

  {
    id: 'synology-ds1621-plus',
    manufacturer: 'Synology',
    model: 'DS1621+',
    category: 'nvr-recorder',
    tagline: '6-bay AMD Ryzen NAS, 40-channel Surveillance Station capacity',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/ProductSpec/DiskStation/21-year/DS1621+/enu/Product_Spec_DS1621+_enu.pdf',
    specs: {
      channels: 40,
      hddBays: 6,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', 'SHR', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '4× 1GbE RJ-45',
    },
  },

  {
    id: 'synology-ds1821-plus',
    manufacturer: 'Synology',
    model: 'DS1821+',
    category: 'nvr-recorder',
    tagline: '8-bay AMD Ryzen DiskStation, Surveillance Station up to 40 cameras',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/ProductSpec/DiskStation/21-year/DS1821+/enu/Product_Spec_DS1821+_enu.pdf',
    specs: {
      channels: 40,
      hddBays: 8,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', 'SHR', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '4× 1GbE RJ-45',
    },
  },

  {
    id: 'synology-ds423-plus',
    manufacturer: 'Synology',
    model: 'DS423+',
    category: 'nvr-recorder',
    tagline: 'Compact 4-bay Celeron NAS with 40-channel Surveillance Station support',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/ProductSpec/DiskStation/23-year/DS423+/enu/Product_Spec_DS423+_enu.pdf',
    specs: {
      channels: 40,
      hddBays: 4,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', 'SHR', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ-45',
    },
  },

  {
    id: 'synology-ds224-plus',
    manufacturer: 'Synology',
    model: 'DS224+',
    category: 'nvr-recorder',
    tagline: 'Entry 2-bay NAS running Surveillance Station up to 25 cameras',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/ProductSpec/DiskStation/24-year/DS224+/enu/Product_Spec_DS224+_enu.pdf',
    specs: {
      channels: 25,
      hddBays: 2,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 1GbE RJ-45',
    },
  },

  {
    id: 'synology-rs822-plus',
    manufacturer: 'Synology',
    model: 'RS822+',
    category: 'nvr-recorder',
    tagline: '1U 4-bay rackmount with Surveillance Station up to 40 cameras',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/ProductSpec/RackStation/22-year/RS822+/enu/Product_Spec_RS822+_enu.pdf',
    specs: {
      channels: 40,
      hddBays: 4,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', 'SHR', 'JBOD'],
      formFactor: '1U',
      networkPorts: '4× 1GbE RJ-45',
    },
  },

  {
    id: 'synology-rs1221-plus',
    manufacturer: 'Synology',
    model: 'RS1221+',
    category: 'nvr-recorder',
    tagline: '2U 8-bay AMD Ryzen rack NAS, Surveillance Station up to 40 cameras',
    verified: true,
    datasheetUrl: 'https://global.download.synology.com/download/Document/Hardware/ProductSpec/RackStation/21-year/RS1221+/enu/Product_Spec_RS1221+_enu.pdf',
    specs: {
      channels: 40,
      hddBays: 8,
      maxResolution: '4K',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', 'SHR', 'JBOD'],
      formFactor: '2U',
      networkPorts: '4× 1GbE RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // QNAP — QGD edge PoE switches + NAS-based QVR Pro hosts
  //   Channels set to 128 (QVR Pro system license cap). QNAP does
  //   not publish per-model recommended-camera counts, so this is
  //   the manufacturer-stated maximum at the software level.
  // ─────────────────────────────────────────────────────────────

  {
    id: 'qnap-qgd-1602p',
    manufacturer: 'QNAP',
    model: 'QGD-1602P',
    category: 'nvr-recorder',
    tagline: 'Smart edge PoE++ switch + NAS/NVR hybrid with 2.5GbE and 10GbE uplinks (QVR Pro up to 128 ch)',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/qgd-1602p',
    specs: {
      channels: 128,
      hddBays: 2,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '50', '60', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 10GbE SFP+, 8× 2.5GbE RJ-45, 8× 1GbE RJ-45',
      poeInPorts: 16,
      poeBudgetW: 370,
    },
  },

  {
    id: 'qnap-qgd-1600p',
    manufacturer: 'QNAP',
    model: 'QGD-1600P',
    category: 'nvr-recorder',
    tagline: 'Smart PoE edge switch running QTS and QVR Pro as combined NVR (up to 128 ch w/ license)',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/qgd-1600p',
    specs: {
      channels: 128,
      hddBays: 2,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '50', '60', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '16× 1GbE RJ-45 (incl. 2 SFP combo)',
      poeInPorts: 16,
      poeBudgetW: 360,
    },
  },

  {
    id: 'qnap-ts-464',
    manufacturer: 'QNAP',
    model: 'TS-464',
    category: 'nvr-recorder',
    tagline: '4-bay 2.5GbE Celeron NAS for QVR Pro surveillance (license up to 128 ch)',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/ts-464',
    specs: {
      channels: 128,
      hddBays: 4,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', '50', '60', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 2.5GbE RJ-45',
    },
  },

  {
    id: 'qnap-ts-664',
    manufacturer: 'QNAP',
    model: 'TS-664',
    category: 'nvr-recorder',
    tagline: '6-bay 2.5GbE Celeron NAS, QVR Pro surveillance ready (license up to 128 ch)',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/ts-664',
    specs: {
      channels: 128,
      hddBays: 6,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', '50', '60', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 2.5GbE RJ-45',
    },
  },

  {
    id: 'qnap-ts-873a',
    manufacturer: 'QNAP',
    model: 'TS-873A',
    category: 'nvr-recorder',
    tagline: '8-bay AMD Ryzen NAS with QVR Pro and PCIe expansion for 10GbE',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/ts-873a',
    specs: {
      channels: 128,
      hddBays: 8,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', '50', '60', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 2.5GbE RJ-45',
    },
  },

  {
    id: 'qnap-ts-873aeu',
    manufacturer: 'QNAP',
    model: 'TS-873AeU',
    category: 'nvr-recorder',
    tagline: '2U short-depth 8-bay rackmount NAS with AMD Ryzen for QVR Pro',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/ts-873aeu',
    specs: {
      channels: 128,
      hddBays: 8,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', '50', '60', 'JBOD'],
      formFactor: '2U',
      networkPorts: '2× 2.5GbE RJ-45',
    },
  },

  {
    id: 'qnap-ts-1232pxu-rp',
    manufacturer: 'QNAP',
    model: 'TS-1232PXU-RP',
    category: 'nvr-recorder',
    tagline: '2U 12-bay rack NAS with 10GbE SFP+, redundant PSU, QVR Pro',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/ts-1232pxu-rp',
    specs: {
      channels: 128,
      hddBays: 12,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', '50', '60', 'JBOD'],
      formFactor: '2U',
      networkPorts: '2× 10GbE SFP+, 2× 2.5GbE RJ-45',
    },
  },

  {
    id: 'qnap-tvs-h874',
    manufacturer: 'QNAP',
    model: 'TVS-h874',
    category: 'nvr-recorder',
    tagline: 'ZFS-based 8-bay Intel Core i5/i7 NAS for QVR Pro and virtualization',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/tvs-h874',
    specs: {
      channels: 128,
      hddBays: 8,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', '50', '60', 'JBOD'],
      formFactor: 'desktop',
      networkPorts: '2× 2.5GbE RJ-45',
    },
  },

  {
    id: 'qnap-tvs-1672xu-rp',
    manufacturer: 'QNAP',
    model: 'TVS-1672XU-RP',
    category: 'nvr-recorder',
    tagline: '3U 16-bay rack NAS with Intel Core i3, 10GbE SmartNIC, redundant PSU',
    verified: true,
    datasheetUrl: 'https://www.qnap.com/en/product/tvs-1672xu-rp',
    specs: {
      channels: 128,
      hddBays: 16,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      raidLevels: ['0', '1', '5', '6', '10', '50', '60', 'JBOD'],
      formFactor: '3U',
      networkPorts: '2× 10GbE SFP+, 4× 1GbE RJ-45',
    },
  },
];
